import React, {createContext, useReducer, useEffect, useState} from 'react';
import {useLocalStorage} from 'react-use';
import {Backend} from './backend';
import {extract} from './utils/openapi-extractor';
import {reducer} from './state-reducer';

const AppContext = createContext({});
const initialState = {breadcrumb: []};

// Resolve identity from the portal-supplied prop first, then fall back
// to window.username (set by index.html's bootstrap shim from query
// params, dev cookies, or VITE_* env). Prop wins if present — even an
// empty/null prop suppresses the legacy fallback so portal can
// explicitly assert "no user". Returns null when truly anonymous.
//
// Username (the path component used by the per-user git sandbox) is
// derived from the email's local part when the portal sends a user;
// the existing window.username already carries the same semantic in
// standalone / dev mode. Two users sharing an email local-part within
// a tenant would collide on disk — portal's email-uniqueness guarantee
// is what keeps that from happening.
function resolveUser(propUser) {
    if (propUser !== undefined) {
        if (!propUser) return null;
        const username = propUser.email ? propUser.email.split('@')[0] : propUser.id;
        return {
            username,
            role: propUser.role,
            email: propUser.email,
            display_name: propUser.display_name,
            id: propUser.id,
            customerId: propUser.customerId || (typeof window !== 'undefined' ? window.customerId : undefined),
        };
    }
    if (typeof window !== 'undefined' && window.username) {
        return {
            username: window.username,
            role: window.userRole,
            email: window.userEmail,
            customerId: window.customerId,
        };
    }
    return null;
}

function AppContextProvider({children, accessToken, user: propUser, editorProps}) {
    const [data, setData] = useLocalStorage('acuwize_ctx', initialState);
    const [state, dispatch] = useReducer(reducer, data);
    const [backend, setBackend] = useState();
    // editorProps surfaced on context so deep views (ApiView's right
    // column, comment-anchor emitters) can read portal-handoff config
    // without prop drilling. Default to empty object so consumers can
    // safely destructure in standalone mode.
    let value = {state, dispatch, backend, extract, editorProps: editorProps || {}};

    useEffect(() => {
        setData(state);
    }, [state]);

    useEffect(() => {
        const user = resolveUser(propUser);
        if (!user) {
            return;
        }
        // Persist identity as cookies for browser-driven resource fetches
        // (<img src=...>, stylesheets) that don't go through axios — those
        // still need user context for the server's dev-mode enforce-role
        // middleware. Skipped in prop-driven (portal) mode because portal's
        // session-cookie middleware is the canonical source there.
        if (!propUser && typeof document !== 'undefined') {
            document.cookie = `dev_user=${encodeURIComponent(user.username || '')}; path=/; SameSite=Lax`;
            document.cookie = `dev_role=${encodeURIComponent(user.role || '')}; path=/; SameSite=Lax`;
            document.cookie = `dev_email=${encodeURIComponent(user.email || '')}; path=/; SameSite=Lax`;
        }
        dispatch({type: 'authenticate', username: user.username, role: user.role, accessToken});
        setBackend(new Backend({
            // Backend URL resolution. In standalone mode the dev shim in
            // editor/index.html sets window.serverUrl from VITE_SERVER_URL;
            // in portal-mounted mode neither is set (portal's vite build
            // doesn't run our dev shim and has no VITE_SERVER_URL). Fall
            // back to '' so axios uses relative URLs — same-origin against
            // whatever Caddy is fronting works without zero config.
            // (Gap #5 from portal Day 11 integration.)
            url: (typeof window !== 'undefined' && window.serverUrl) || import.meta.env.VITE_SERVER_URL || '',
            username: user.username,
            role: user.role,
            email: user.email,
            customerId: user.customerId,
            accessToken,
        }));
    }, [propUser, accessToken]);

    return backend ? <AppContext.Provider value={value}>{children}</AppContext.Provider> : null;
}

const AppContextConsumer = AppContext.Consumer;

export {AppContext, AppContextProvider, AppContextConsumer};
